import { supabase } from '../lib/supabase';

export const contactService = {
  // Submit a new contact request
  async submitContactRequest(requestData) {
    try {
      // Prepare the data with proper validation and defaults
      const insertData = {
        user_id: requestData?.user_id || null,
        name: requestData?.name?.trim(),
        email: requestData?.email?.trim(),
        subject: requestData?.subject?.trim(),
        message: requestData?.message?.trim(),
        support_type: requestData?.supportType || 'other',
        occupation: requestData?.occupation?.trim() || null,
        experience_level: requestData?.experienceLevel || null,
        current_zip: requestData?.currentZip?.trim() || null,
        target_zip: requestData?.targetZip?.trim() || null,
        account_tier: requestData?.accountTier || 'free',
        top_skills: requestData?.topSkills?.trim() || null,
        target_role: requestData?.targetRole?.trim() || null,
        device: requestData?.device?.trim() || null,
        steps_to_reproduce: requestData?.steps?.trim() || null,
        consent: Boolean(requestData?.consent)
      };

      // Client-side validation
      if (!insertData?.name || insertData?.name === '') {
        return { 
          data: null, 
          error: 'Name is required' 
        };
      }

      if (!insertData?.email || insertData?.email === '') {
        return { 
          data: null, 
          error: 'Email is required' 
        };
      }

      if (!insertData?.subject || insertData?.subject === '') {
        return { 
          data: null, 
          error: 'Subject is required' 
        };
      }

      if (!insertData?.message || insertData?.message === '') {
        return { 
          data: null, 
          error: 'Message is required' 
        };
      }

      if (!insertData?.consent) {
        return { 
          data: null, 
          error: 'Consent is required' 
        };
      }

      console.log('Submitting contact request:', insertData);
      console.log('Current auth user:', await supabase.auth.getUser());

      // Try direct insert first
      let { data, error } = await supabase
        ?.from('contact_requests')
        ?.insert([insertData])
        ?.select()
        ?.single();

      // If RLS policy fails, try using a stored procedure as fallback
      if (error && (error?.code === '42501' || error?.message?.includes('policy'))) {
        console.log('RLS policy failed, trying stored procedure fallback...');
        
        // Try using a stored procedure that bypasses RLS
        const { data: rpcData, error: rpcError } = await supabase
          ?.rpc('submit_contact_request', {
            p_name: insertData.name,
            p_email: insertData.email,
            p_subject: insertData.subject,
            p_message: insertData.message,
            p_support_type: insertData.support_type,
            p_occupation: insertData.occupation,
            p_experience_level: insertData.experience_level,
            p_current_zip: insertData.current_zip,
            p_target_zip: insertData.target_zip,
            p_account_tier: insertData.account_tier,
            p_top_skills: insertData.top_skills,
            p_target_role: insertData.target_role,
            p_device: insertData.device,
            p_steps_to_reproduce: insertData.steps_to_reproduce,
            p_consent: insertData.consent,
            p_user_id: insertData.user_id
          });

        if (rpcError) {
          console.error('RPC fallback also failed:', rpcError);
          error = rpcError;
        } else {
          // The RPC now returns JSON, so we need to handle it properly
          data = rpcData;
          error = null;
        }
      }

      if (error) {
        console.error('Database error details:', error);
        
      // Enhanced error handling with specific messages
      if (error?.code === '42501' || error?.message?.includes('policy')) {
        console.error('RLS Policy violation:', error);
        return { 
          data: null, 
          error: 'There was a permission issue submitting your request. Please try again or contact support directly at support@skillbinder.com.' 
        };
      }
        
        if (error?.message?.includes('invalid input value')) {
          return { 
            data: null, 
            error: 'Invalid data provided. Please refresh the page and try again.' 
          };
        }

        if (error?.message?.includes('duplicate') || error?.code === '23505') {
          return { 
            data: null, 
            error: 'A request with this information already exists.' 
          };
        }

        return { 
          data: null, 
          error: `Failed to submit request: ${error?.message || 'Please try again.'}` 
        };
      }

      console.log('Contact request submitted successfully:', data);

      // Send admin notification email (non-blocking)
      try {
        console.log('Sending admin notification email...', new Date().toISOString());
        const adminEmailResult = await supabase?.functions?.invoke('contact-notification', {
          body: { contactData: insertData }
        });
        console.log('Admin email result:', adminEmailResult);
      } catch (emailError) {
        console.warn('Admin email notification failed:', emailError);
      }

      // Send user confirmation email (non-blocking)
      try {
        console.log('Sending user confirmation email...', new Date().toISOString());
        const userEmailResult = await supabase?.functions?.invoke('send-user-confirmation', {
          body: { contactData: insertData }
        });
        console.log('User confirmation email result:', userEmailResult);
      } catch (emailError) {
        console.warn('User confirmation email failed:', emailError);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Unexpected error in contactService.submitContactRequest:', error);
      
      // Network/connection error handling
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          data: null, 
          error: 'Cannot connect to server. Please check your internet connection and try again.' 
        };
      }
      
      return { 
        data: null, 
        error: 'An unexpected error occurred. Please try again later.' 
      };
    }
  },

  // Get contact requests for the current user
  async getUserContactRequests(userId) {
    if (!userId) return { data: [], error: null };
    
    try {
      const { data, error } = await supabase?.from('contact_requests')?.select(`
          *,
          responses:contact_responses(
            id,
            message,
            is_internal,
            created_at,
            responder:responder_id(full_name)
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  // Get a specific contact request with responses
  async getContactRequest(requestId) {
    try {
      const { data, error } = await supabase?.from('contact_requests')?.select(`
          *,
          responses:contact_responses(
            id,
            message,
            is_internal,
            created_at,
            responder:responder_id(full_name)
          ),
          attachments:contact_attachments(
            id,
            file_name,
            file_path,
            file_size,
            mime_type,
            created_at
          )
        `)?.eq('id', requestId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get all contact requests (admin only)
  async getAllContactRequests(filters = {}) {
    try {
      let query = supabase?.from('contact_requests')?.select(`
          *,
          user:user_profiles(full_name, email),
          responses:contact_responses(count)
        `);

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.support_type) {
        query = query?.eq('support_type', filters?.support_type);
      }
      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }
      if (filters?.assigned_to) {
        query = query?.eq('assigned_to', filters?.assigned_to);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  // Update contact request (admin only)
  async updateContactRequest(requestId, updates) {
    try {
      const { data, error } = await supabase?.from('contact_requests')?.update(updates)?.eq('id', requestId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Add response to contact request
  async addContactResponse(responseData) {
    try {
      const { data, error } = await supabase?.from('contact_responses')?.insert([{
          contact_request_id: responseData?.contact_request_id,
          responder_id: responseData?.responder_id,
          message: responseData?.message,
          is_internal: responseData?.is_internal || false
        }])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get support statistics (admin only)
  async getSupportStatistics() {
    try {
      const { data: stats, error } = await supabase?.rpc('get_support_statistics');

      if (error) throw error;
      return { data: stats || {}, error: null };
    } catch (error) {
      return { data: {}, error };
    }
  },

  // Subscribe to real-time contact request changes
  subscribeToContactRequests(callback, userId = null) {
    const channel = supabase?.channel('contact-requests-changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contact_requests',
          filter: userId ? `user_id=eq.${userId}` : undefined
        },
        callback
      )?.subscribe();

    return channel;
  },

  // Unsubscribe from real-time changes
  unsubscribeFromContactRequests(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};